import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormLabel, Grid, Tabs, Tooltip } from '@mui/material';
import { withTranslation } from 'react-i18next';
import { Autocomplete, Button, Card, Typography } from '@totalsoft/rocket-ui';
import FilterList from '@mui/icons-material/FilterList';
import ReorderIcon from '@mui/icons-material/Reorder';
import WindowIcon from '@mui/icons-material/Window';
import { onSliderChange } from 'utils/propertyChangeAdapters';
import { ParamsContext } from 'providers/ParamsContext';
import FilterSlider from './FilterSlider';
import ThinDivider from 'components/common/ThinDivider';
import {
  FilterSliderFieldLabelText,
  FilterSliderFieldLabelWrapper,
  FilterSliderFieldValue,
  FilterSliderFieldWrapper,
  FilterSliderLabelWrapper,
  FilterSliderLoadingFormControl,
  ProductSelectionFiltersTitle,
  ProductSelectionViewModesTab,
  ProductSelectionViewModesWrapper
} from './ProductSelectionStyles';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';
import { useSelector } from 'react-redux';

export const ProductSelectionFiltersComponent = ({
  filterData,
  t,
  onFilterChange,
  onPushFilterValues,
  numberOfProducts,
  flags,
  setProductsViewMode,
  productsViewMode,
  orderObject,
  orderOptions,
  onOrderChange,
  processing,
  contractTypeNames,
  onContractTypeLinkClick,
  showFilterOfferContracts,
  financialProductWrite,
  readOnlyFull,
  onPropertyChange
}) => {
  const { validationInfo } = filterData;
  const financingPeriodFilter = filterData.document.financingPeriod;
  const advancePercentFilter = filterData.document.advancePercent;
  const residualValuePercentFilter = filterData.document.residualValuePercent;
  const { cvsChPercent, cvsChAmount } = useContext(ParamsContext);
  const generationStatusInProgress = useSelector(state => state.currentOffer.productSelectionData.flags.simulationsGenerationInProgress);
  const [valueLabelOptions, setValueLabelOptions] = useState([{ value: '%', label: '%' }]);

  useEffect(() => {
    const option = valueLabelOptions.find(option => option.value === filterData.document.currencyCode);

    if (!option) {
      setValueLabelOptions(prevOptions => [
        ...prevOptions,
        {
          value: filterData.document.currencyCode,
          label: filterData.document.currencyCode
        }
      ]);
    }
  }, [filterData.document.currencyCode, valueLabelOptions]);

  const financingPeriodSliderValueFormat = value => {
    return `${value} ${t('OfferEdit.FinancialProducts.Months')}`;
  };

  /* ADVANCE FIELD */
  const [advanceFieldValue, setAdvanceFieldValue] = useState(
    advancePercentFilter.value == null
      ? advancePercentFilter.minValue
      : advancePercentFilter.value < advancePercentFilter.minValue
      ? advancePercentFilter.minValue
      : advancePercentFilter.value
  );
  const [advanceFieldLabel, setAdvanceFieldLabel] = useState('%');
  const [advanceFieldDecimalScale, setAdvanceFieldDecimalScale] = useState(cvsChPercent);
  const handleAdvanceFieldValueChange = value => {
    setAdvanceFieldValue(value);
    if (advanceFieldLabel === '%') {
      return value;
    } else {
      return (value / filterData.document.acquisitionPrice) * 100;
    }
  };
  const handleAdvanceValueChangeForFieldValue = value => {
    if (advanceFieldLabel === '%') {
      setAdvanceFieldValue(value);
    } else {
      const advanceValueTmp = (filterData.document.acquisitionPrice * value) / 100;
      setAdvanceFieldValue(advanceValueTmp);
    }
  };
  const handleAdvanceFieldLabelChange = value => {
    if (value === advanceFieldLabel) return;

    setAdvanceFieldLabel(value);
    if (value === '%') {
      setAdvanceFieldDecimalScale(cvsChPercent);
      setAdvanceFieldValue(advancePercentFilter.value);
    } else {
      const advanceValueTmp = (filterData.document.acquisitionPrice * advancePercentFilter.value) / 100;
      setAdvanceFieldDecimalScale(cvsChAmount);
      setAdvanceFieldValue(advanceValueTmp);
    }
  };
  const advanceValueSliderValueFormat = _ => {
    if (advanceFieldLabel === '%') {
      const tmpValue = (filterData.document.acquisitionPrice * advanceFieldValue) / 100;
      return `${tmpValue.toFixed(cvsChAmount)} ${filterData.document.currencyCode}`;
    }
    const tmpValuePercent = (advanceFieldValue / filterData.document.acquisitionPrice) * 100;
    return `${tmpValuePercent.toFixed(cvsChPercent)} %`;
  };
  /* END ADVANCE FIELD */

  /* RESIDUAL FIELD */
  const [residualFieldValue, setResidualFieldValue] = useState(
    residualValuePercentFilter.value == null
      ? residualValuePercentFilter.minValue
      : residualValuePercentFilter.value < residualValuePercentFilter.minValue
      ? residualValuePercentFilter.minValue
      : residualValuePercentFilter.value
  );
  const [residualFieldLabel, setResidualFieldLabel] = useState('%');
  const [residualFieldDecimalScale, setResidualFieldDecimalScale] = useState(cvsChPercent);
  const handleResidualFieldValueChange = value => {
    setResidualFieldValue(value);
    if (residualFieldLabel === '%') {
      return value;
    } else {
      return (value / filterData.document.acquisitionPrice) * 100;
    }
  };
  const handleResidualValueChangeForFieldValue = value => {
    if (residualFieldLabel === '%') {
      setResidualFieldValue(value);
    } else {
      const residualValueTmp = (filterData.document.acquisitionPrice * value) / 100;
      setResidualFieldValue(residualValueTmp);
    }
  };
  const handleResidualFieldLabelChange = value => {
    if (value === residualFieldLabel) return;

    setResidualFieldLabel(value);
    if (value === '%') {
      setResidualFieldDecimalScale(cvsChPercent);
      setResidualFieldValue(residualValuePercentFilter.value);
    } else {
      const residualValueTmp = (filterData.document.acquisitionPrice * residualValuePercentFilter.value) / 100;
      setResidualFieldDecimalScale(cvsChAmount);
      setResidualFieldValue(residualValueTmp);
    }
  };
  const residualValueSliderValueFormat = _ => {
    if (residualFieldLabel === '%') {
      const tmpValue = (filterData.document.acquisitionPrice * residualFieldValue) / 100;
      return `${tmpValue.toFixed(cvsChAmount)} ${filterData.document.currencyCode}`;
    }
    const tmpValuePercent = (residualFieldValue / filterData.document.acquisitionPrice) * 100;
    return `${tmpValuePercent.toFixed(cvsChPercent)} %`;
  };
  /* END RESIDUAL FIELD */

  const modalLabel = (
    <Grid container sx={{ fontSize: '18px', fontWeight: 'bold' }}>
      {t('OfferEdit.FinancialProducts.Filters.Title1', {
        data: { totalProducts: numberOfProducts.total }
      })}
      {readOnlyFull || !financialProductWrite ? (
        <ProductSelectionFiltersTitle onClick={onContractTypeLinkClick} underline='always' readOnly>
          {contractTypeNames?.length > 0 ? contractTypeNames : t('OfferEdit.FinancialProducts.Filters.NoFinancingType')}
        </ProductSelectionFiltersTitle>
      ) : (
        <ProductSelectionFiltersTitle onClick={onContractTypeLinkClick} underline='always'>
          {contractTypeNames?.length > 0 ? contractTypeNames : t('OfferEdit.FinancialProducts.Filters.NoFinancingType')}
        </ProductSelectionFiltersTitle>
      )}
      {t('OfferEdit.FinancialProducts.Filters.Title2', {
        data: { availableProducts: numberOfProducts.available }
      })}
    </Grid>
  );

  const simpleLabel = (
    <div>
      {t('OfferEdit.FinancialProducts.Filters.Title', {
        data: { totalProducts: numberOfProducts.total, availableProducts: numberOfProducts.available }
      })}
    </div>
  );

  return (
    <Card icon={FilterList} title={showFilterOfferContracts ? modalLabel : simpleLabel}>
      <Grid container spacing={3} alignItems='center'>
        <Grid item xs={12} sm={12} lg={12} xl={11}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} xl>
              <FilterSlider
                id={'productSelectionFilters_financingPeriod'}
                fullWidth
                required
                value={financingPeriodFilter.value}
                fieldLabel={t('OfferEdit.FinancialProducts.Months')}
                valueLabelFormat={financingPeriodSliderValueFormat}
                min={financingPeriodFilter.minValue}
                max={financingPeriodFilter.maxValue}
                step={financingPeriodFilter.step}
                label={t('OfferEdit.FinancialProducts.FinancingPeriod')}
                disabled={readOnlyFull || !financialProductWrite || flags.computingSimulations || processing}
                onChange={onSliderChange(onFilterChange('financingPeriod'), financingPeriodFilter.minValue, financingPeriodFilter.maxValue)}
                error={validationInfo.financingPeriod && Boolean(validationInfo.financingPeriod.value)}
                helperText={validationInfo.financingPeriod ? validationInfo.financingPeriod.value : ''}
                decimalScale={0}
                showSliderLimits={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl>
              <FilterSlider
                id={'productSelectionFilters_advancePercent'}
                fullWidth
                required
                value={advancePercentFilter.value}
                fieldValue={advanceFieldValue}
                onFieldValueChange={handleAdvanceFieldValueChange}
                onValueChangeForFieldValue={handleAdvanceValueChangeForFieldValue}
                fieldDecimalScale={advanceFieldDecimalScale}
                fieldLabelOptions={valueLabelOptions}
                fieldLabel={advanceFieldLabel}
                onFieldLabelChange={handleAdvanceFieldLabelChange}
                valueLabelFormat={advanceValueSliderValueFormat}
                min={advancePercentFilter.minValue}
                max={advancePercentFilter.maxValue}
                minMaxLabel={'%'}
                step={advancePercentFilter.step}
                label={t('OfferEdit.FinancialProducts.Advance')}
                disabled={readOnlyFull || !financialProductWrite || flags.computingSimulations || processing}
                onChange={onSliderChange(onFilterChange('advancePercent'), advancePercentFilter.minValue, advancePercentFilter.maxValue)}
                error={validationInfo.advancePercent && Boolean(validationInfo.advancePercent.value)}
                helperText={validationInfo.advancePercent ? validationInfo.advancePercent.value : ''}
                decimalScale={cvsChPercent}
                showSliderLimits={true}
              />
            </Grid>
            <Grid item container xs={12} sm={6} xl alignItems='center'>
              {flags.maxResidualValuePercentLoading ? (
                <FilterSliderLoadingFormControl>
                  <FormLabel>{t('OfferEdit.FinancingStructure.ResidualValue')}</FormLabel>
                  <WrappedLinearProgress />
                </FilterSliderLoadingFormControl>
              ) : (
                <FilterSlider
                  id={'productSelectionFilters_residualValuePercent'}
                  fullWidth
                  required
                  value={residualValuePercentFilter.value}
                  fieldValue={residualFieldValue}
                  onFieldValueChange={handleResidualFieldValueChange}
                  onValueChangeForFieldValue={handleResidualValueChangeForFieldValue}
                  fieldDecimalScale={residualFieldDecimalScale}
                  fieldLabel={residualFieldLabel}
                  fieldLabelOptions={valueLabelOptions}
                  onFieldLabelChange={handleResidualFieldLabelChange}
                  valueLabelFormat={residualValueSliderValueFormat}
                  min={residualValuePercentFilter.minValue}
                  max={residualValuePercentFilter.maxValue}
                  minMaxLabel={'%'}
                  step={residualValuePercentFilter.step}
                  label={t('OfferEdit.FinancialProducts.ResidualValue')}
                  disabled={
                    readOnlyFull ||
                    !financialProductWrite ||
                    flags.computingSimulations ||
                    processing ||
                    flags.maxResidualValuePercentLoading
                  }
                  onChange={onSliderChange(
                    onFilterChange('residualValuePercent'),
                    residualValuePercentFilter.minValue,
                    residualValuePercentFilter.maxValue
                  )}
                  error={validationInfo.residualValuePercent && Boolean(validationInfo.residualValuePercent.value)}
                  helperText={validationInfo.residualValuePercent ? validationInfo.residualValuePercent.value : ''}
                  decimalScale={cvsChPercent}
                  showSliderLimits={true}
                />
              )}
            </Grid>
            {filterData.document.agreedMileageFilter && (
              <Grid item xs={12} sm={6} xl>
                <Grid container pb={1} alignContent='center'>
                  <FilterSliderLabelWrapper item xs>
                    <Typography variant='caption' fontWeight='bold'>
                      {t('OfferEdit.FinancialProducts.AgreedMileage')}
                    </Typography>
                  </FilterSliderLabelWrapper>
                  <FilterSliderFieldWrapper item container>
                    <Grid item>
                      <FilterSliderFieldValue
                        debounceBy={500}
                        id={'productSelectionFilters_agreedMileage'}
                        isNumeric
                        value={filterData.document.agreedMileage}
                        onChange={onPropertyChange('agreedMileage')}
                        inputProps={{ decimalScale: 0, color: 'red' }}
                        InputProps={{ disableUnderline: true, color: 'red' }}
                        disabled={readOnlyFull || !financialProductWrite || flags.computingSimulations || processing}
                      />
                    </Grid>
                    <FilterSliderFieldLabelWrapper item container alignItems='center' height={1}>
                      <FilterSliderFieldLabelText>Km</FilterSliderFieldLabelText>
                    </FilterSliderFieldLabelWrapper>
                  </FilterSliderFieldWrapper>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={3} md={5} lg={2} xl={1}>
          <Button
            fullWidth
            id={'productSelectionFilters_calculate'}
            onClick={onPushFilterValues}
            disabled={
              !validationInfo.isValid ||
              readOnlyFull ||
              flags.maxResidualValuePercentLoading ||
              !financialProductWrite ||
              flags.computingSimulations ||
              processing ||
              flags.generateSimulationsButtonDisabled || generationStatusInProgress
            }
          >
            {t('OfferEdit.FinancialProducts.Calculate')}
          </Button>
        </Grid>
        <Grid item xs={12} p={0}>
          <ThinDivider />
        </Grid>
        <Grid container item spacing={1}>
          <Grid item xs={4}>
            <Autocomplete
              fullWidth
              id={'productSelectionFilters_orderBy'}
              label={t('Tooltips.OrderBy')}
              value={orderObject.orderId}
              valueKey={'orderId'}
              labelKey={'orderName'}
              onChange={onOrderChange}
              options={orderOptions}
              simpleValue
              isClearable={false}
            />
          </Grid>
          <ProductSelectionViewModesWrapper item>
            {t('OfferEdit.FinancialProducts.ViewMode')}
            <Tabs
              id={'productSelectionFilters_viewMode'}
              value={productsViewMode}
              onChange={(e, index) => setProductsViewMode(index)}
              centered
            >
              <Tooltip title={t('Tooltips.GridView')}>
                <ProductSelectionViewModesTab
                  id={'productSelectionFilters_viewMode_grid'}
                  icon={<WindowIcon />}
                  disabled={flags.computingSimulations || generationStatusInProgress}
                ></ProductSelectionViewModesTab>
              </Tooltip>
              <Tooltip title={t('Tooltips.ListView')}>
                <ProductSelectionViewModesTab
                  id={'productSelectionFilters_viewMode_list'}
                  icon={<ReorderIcon />}
                  disabled={flags.computingSimulations || generationStatusInProgress}
                ></ProductSelectionViewModesTab>
              </Tooltip>
            </Tabs>
          </ProductSelectionViewModesWrapper>
        </Grid>
      </Grid>
    </Card>
  );
};

ProductSelectionFiltersComponent.propTypes = {
  t: PropTypes.func.isRequired,
  filterData: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onPushFilterValues: PropTypes.func.isRequired,
  flags: PropTypes.object.isRequired,
  numberOfProducts: PropTypes.object.isRequired,
  setProductsViewMode: PropTypes.func.isRequired,
  productsViewMode: PropTypes.number.isRequired,
  orderOptions: PropTypes.array.isRequired,
  orderObject: PropTypes.object.isRequired,
  onOrderChange: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  contractTypeNames: PropTypes.string,
  onContractTypeLinkClick: PropTypes.func.isRequired,
  showFilterOfferContracts: PropTypes.bool.isRequired,
  financialProductWrite: PropTypes.bool,
  readOnlyFull: PropTypes.bool,
  onPropertyChange: PropTypes.func.isRequired
};

export default withTranslation()(ProductSelectionFiltersComponent);
